<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePortalStore } from '@/stores/portal.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useClone } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useDetectDevice } from '@/composables/device'
import { useValidation } from '@/composables/validation'

import NavigateBack from '@/components/shared/NavigateBack.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const portalStore = usePortalStore()
const planningStore = usePlanningStore()
const { emailValidator } = useValidation()

const { referenceTypeEnum } = planningStore
const referenceTypeOptions = portalStore.getLocalizedReferenceTypes()

const form = ref(null)
const reference = ref({})

const emit = defineEmits(['save', 'cancel'])
useNavigator(() => emit('cancel'))

onMounted(() => {
  reference.value = useClone(props.modelValue)
})

const requiredRule = (val) => (!!val && String(val).length > 0) || i18n.t('common.required')
const emailRule = (val) => reference.value.reference_type !== referenceTypeEnum.EMAIL || emailValidator(val) || i18n.t('error.invalidEmail')

const isEmailInvalid = computed(() =>
  reference.value.reference_type === referenceTypeEnum.EMAIL && !!reference.value.value && !emailValidator(reference.value.value)
)

const onSave = async() => {
  const valid = await form.value.validate()
  if (!valid) return
  emit('save', reference.value)
}
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <navigate-back
      :text="reference?.value ? i18n.t('common.reference', 1) : i18n.t('common.addReference')"
      @cancel="emit('cancel')"
    />
    <q-card-section class="scroll col q-pa-md">
      <q-form ref="form">
        <div class="row q-col-gutter-sm items-start">
          <q-select
            v-model="reference.reference_type"
            :class="isMobile ? 'col-5' : 'col-3'"
            :label="i18n.t('common.type')"
            :options="referenceTypeOptions"
            option-value="id"
            option-label="name"
            emit-value
            map-options
          />
          <q-input
            v-model="reference.label"
            :class="isMobile ? 'col-7' : 'col-3'"
            :label="i18n.t('common.label')"
            lazy-rules
            :rules="[requiredRule]"
          />
          <q-input
            v-model="reference.value"
            :class="isMobile ? 'col-12' : 'col-6'"
            :label="i18n.t('common.value')"
            autofocus
            lazy-rules
            :rules="[requiredRule, emailRule]"
          />
          <q-checkbox
            v-model="reference.is_default"
            class="col-12"
            :label="i18n.t('common.default')"
            color="primary"
          />
        </div>
      </q-form>
    </q-card-section>
    <q-card-actions align="right" class="q-pa-md">
      <q-btn
        color="primary"
        :label="i18n.t('common.confirm')"
        :disable="isEmailInvalid"
        @click="onSave"
      />
    </q-card-actions>
  </q-card>
</template>
